<template>
  <v-container fluid class="pa-4">
    <!-- Printable receipt area -->
    <v-card 
      id="receipt"
      class="receipt mx-auto" 
      max-width="400"
      elevation="4"
      style="border: 2px solid var(--v-theme-primary);"
    >
      <!-- Receipt Header -->
      <v-card-title class="tableheader white--text text-center py-4">
        <v-icon size="32" color="white" class="mb-2">mdi-home-city</v-icon>
        <div class="text-h5 font-weight-bold">BAGU RENTALS</div>
        <div class="text-subtitle-2">Property Management</div>
      </v-card-title>

      <!-- Receipt Number and Date -->
      <v-card-text class="pa-4">
        <v-row class="mb-3">
          <v-col cols="6">
            <div class="text-caption text-grey-600">Receipt #</div>
            <div class="text-body-1 font-weight-bold primary--text">
              {{ r.receipt_number || r.id }}
            </div>
          </v-col>
          <v-col cols="6" class="text-right">
            <div class="text-caption text-grey-600">Date</div>
            <div class="text-body-1 font-weight-medium">
              {{ formatDateForDisplay(r.payment_date) }}
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>

        <!-- Payment Details -->
        <div class="mb-4">
          <div class="text-caption text-grey-600 mb-1">PAYMENT DETAILS</div>
          
          <v-row class="mb-2">
            <v-col cols="6">
              <span class="text-body-2 font-weight-medium">Tenant:</span>
            </v-col>
            <v-col cols="6">
              <span class="text-body-2">{{ r.tenant_name }}</span>
            </v-col>
          </v-row>

          <v-row class="mb-2">
            <v-col cols="6">
              <span class="text-body-2 font-weight-medium">Unit:</span>
            </v-col>
            <v-col cols="6">
              <span class="text-body-2">{{ r.unit_name }}</span>
            </v-col>
          </v-row>

          <v-row class="mb-2">
            <v-col cols="6">
              <span class="text-body-2 font-weight-medium">Payment Method:</span>
            </v-col>
            <v-col cols="6">
              <span class="text-body-2">{{ r.payment_method || 'N/A' }}</span>
            </v-col>
          </v-row>

          <v-row class="mb-2">
            <v-col cols="6">
              <span class="text-body-2 font-weight-medium">Status:</span>
            </v-col>
            <v-col cols="6">
              <v-chip 
                :color="r.status === 'completed' ? 'success' : 'warning'" 
                size="small" 
                variant="tonal"
              >
                {{ r.status || 'N/A' }}
              </v-chip>
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-3"></v-divider>

        <!-- Amount Section -->
        <div class="text-center mb-4">
          <div class="text-caption text-grey-600 mb-1">AMOUNT PAID</div>
          <div class="text-h4 font-weight-bold primary--text">
            {{ formatCurrency(r.amount_paid) }}
          </div>
        </div>

        <v-divider class="my-3"></v-divider>

        <!-- Additional Details -->
        <div v-if="r.payment_details" class="mb-4">
          <div class="text-caption text-grey-600 mb-1">NOTES</div>
          <div class="text-body-2">{{ r.payment_details }}</div>
        </div>

        <!-- Footer -->
        <div class="text-center">
          <div class="text-caption text-grey-600 mb-2">
            Thank you for your payment!
          </div>
          <div class="text-caption text-grey-500">
            This receipt serves as proof of payment
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Action buttons (hidden when printing) -->
    <div class="mt-6 text-center no-print">
      <v-btn 
        color="primary" 
        variant="elevated" 
        size="large"
        @click="goToPayments"
        class="mx-2"
      >
        <v-icon left>mdi-arrow-left</v-icon>
        Back to Payments
      </v-btn>
      
      <v-btn 
        color="secondary" 
        variant="elevated" 
        size="large"
        @click="downloadPdf"
        class="mx-2"
      >
        <v-icon left>mdi-download</v-icon>
        Download PDF
      </v-btn>
    </div>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import html2pdf from 'html2pdf.js'
import PaymentService from '@/services/PaymentService'
import TenantService from '@/services/TenantService';
import { formatDateForDisplay, formatCurrency } from '@/services/genFunctions';

const r = ref({})
const route = useRoute()
const router = useRouter()
  
onMounted(async () => {
  try {
    r.value = await PaymentService.getPayment(route.params.id)
    const tenant = await TenantService.getTenant(r.value.tenant_id)
    r.value.tenant_name = tenant.first_name + ' ' + tenant.last_name
  } catch (error) {
    console.error('Error loading payment:', error)
  }
})

const downloadPdf = () => {
  html2pdf()
    .set({
      margin: 10,
      filename: `receipt_${r.value.receipt_number || r.value.id}.pdf`,
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    })
    .from(document.getElementById('receipt'))
    .save()
}

const goToPayments = () => {
  router.push('/payments')
}
</script>

<style scoped>
/* Print styles */
@media print {
  .no-print {
    display: none !important;
  }
  
  body {
    margin: 0;
    padding: 0;
  }
  
  .v-container {
    padding: 0 !important;
  }
  
  .receipt {
    box-shadow: none !important;
    border: 2px solid #1976D2 !important;
    margin: 0 auto !important;
    max-width: 400px !important;
  }
  
  /* Ensure proper page breaks */
  .receipt {
    page-break-inside: avoid;
  }
}

/* Receipt styling */
.receipt {
  background: white;
  font-family: 'Roboto', sans-serif;
}

.primary--text {
  color: var(--v-theme-primary) !important;
}

/* Custom receipt styling */
.v-card-title {
  border-bottom: 2px solid var(--v-theme-primary);
}

.text-h4 {
  font-size: 2rem !important;
  line-height: 1.2 !important;
}

/* Ensure proper spacing for receipt layout */
.v-row {
  margin: 0;
}

.v-col {
  padding: 4px 8px;
}
</style>
